import React from 'react';
import { Dialog, DialogContent, IconButton, Typography, Box, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';

// Import custom icons
import SearchIcon from '../icons/SearchIcon';
import BarChartIcon from '../icons/BarChartIcon';
import TaskIcon from '../icons/TaskIcon';
import ResultsIcon from '../icons/ResultsIcon';
import TrophyIcon from '../icons/TrophyIcon';
import LightbulbIcon from '../icons/LightbulbIcon';
import PeopleIcon from '../icons/PeopleIcon';
import GrowthToolsIcon from '../icons/GrowthToolsIcon';
import ShopIcon from '../icons/ShopIcon';
import MedicalIcon from '../icons/MedicalIcon';
import BuildingIcon from '../icons/BuildingIcon';
import MarketersIcon from '../icons/MarketersIcon';
import EyeIcon from '../icons/EyeIcon';
import TargetIcon from '../icons/TargetIcon';
import GearIcon from '../icons/GearIcon';
import CommunityIcon from '../icons/CommunityIcon';
import CheckmarkIcon from '../icons/CheckmarkIcon';

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '28px',
  fontWeight: 600,
  color: '#ffffff',
  marginBottom: '16px'
}));

const SectionText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '22px',
  color: '#ffffff',
  marginBottom: '24px'
}));

const CardBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#1e293b',
  borderRadius: '12px',
  padding: '20px',
  border: '1px solid #334155',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#22d3ee',
    boxShadow: '0 0 20px rgba(34, 211, 238, 0.2)'
  }
}));

const AboutUsModal = ({ open, onClose }) => {
  const pillars = [
    {
      icon: <SearchIcon width={24} height={24} color="#ffffff" />,
      title: 'Diagnose',
      description: 'We analyze your Google Business Profile and local presence, giving you a clear picture of where you stand.',
      bgColor: '#4CAF50'
    },
    {
      icon: <BarChartIcon width={23} height={21} color="#ffffff" />,
      title: 'Benchmarking',
      description: 'With the Visibeen™ Score️, instantly compare your visibility against competitors and industry standards.',
      bgColor: '#2196F3'
    },
    {
      icon: <TaskIcon width={24} height={24} color="#ffffff" />,
      title: 'Tasks',
      description: 'Get prioritized, AI-powered tasks that help you improve step by step.',
      bgColor: '#4CAF50'
    },
    {
      icon: <ResultsIcon width={24} height={24} color="#ffffff" />,
      title: 'Results',
      description: 'Track measurable growth in visibility, traffic, and leads.',
      bgColor: '#2196F3'
    }
  ];

  const features = [
    {
      icon: <TrophyIcon width={20} height={20} color="#22d3ee" />,
      title: 'Visibeen™ Score️',
      description: 'Your live benchmark of local visibility.'
    },
    {
      icon: <LightbulbIcon width={13} height={18} color="#22d3ee" />,
      title: 'Smart Insights',
      description: 'AI-powered recommendations tailored to your profile.'
    },
    {
      icon: <PeopleIcon width={18} height={13} color="#22d3ee" />,
      title: 'Competitor Edge',
      description: 'Know exactly how you stack up and how to outrank.'
    },
    {
      icon: <GrowthToolsIcon width={20} height={20} color="#22d3ee" />,
      title: 'Growth Tools',
      description: 'From reviews to posts, everything you need to shine locally.'
    }
  ];

  const targetAudiences = [
    {
      icon: <ShopIcon width={36} height={32} color="#22d3ee" />,
      title: 'Local Shops',
      description: 'Attract more walk-ins and increase foot traffic through better online visibility.'
    },
    {
      icon: <MedicalIcon width={36} height={36} color="#22d3ee" />,
      title: 'Clinics & Services',
      description: 'Build trust and reach in your local community.'
    },
    {
      icon: <BuildingIcon width={36} height={36} color="#22d3ee" />,
      title: 'Multi-location Businesses',
      description: 'Manage 25+ profiles across different areas.'
    },
    {
      icon: <MarketersIcon width={36} height={30} color="#22d3ee" />,
      title: 'Marketers & Agencies',
      description: 'Deliver measurable results for your clients.'
    }
  ];

  const values = [
    {
      icon: <EyeIcon width={22} height={15} color="#ffffff" />,
      title: 'Transparency',
      description: 'No jargon, no hidden work. You always know where you stand.',
      bgColor: '#2196F3'
    },
    {
      icon: <LightbulbIcon width={13} height={18} color="#ffffff" />,
      title: 'Simplicity',
      description: 'SEO shouldn\'t be complicated. We make it practical and easy to act on.',
      bgColor: '#FFC107'
    },
    {
      icon: <TargetIcon width={24} height={24} color="#ffffff" />,
      title: 'Impact First',
      description: 'Every feature is designed to create real business results.',
      bgColor: '#2196F3'
    },
    {
      icon: <GearIcon width={18} height={20} color="#ffffff" />,
      title: 'Innovation',
      description: 'We stay ahead of trends so your business does too.',
      bgColor: '#f44336'
    },
    {
      icon: <CommunityIcon width={21} height={20} color="#ffffff" />,
      title: 'Community',
      description: 'We grow when our clients grow. Your success is our success.',
      bgColor: '#3f51b5'
    }
  ];

  const benefits = [
    'Improve rankings faster',
    'Turn visibility into customers',
    'Measurable business impact',
    'Expert guidance every step'
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          background: 'transparent',
          boxShadow: 'none',
          maxHeight: '90vh'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(15,23,42,0.95)',
          backdropFilter: 'blur(8px)'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 900,
            m: 'auto',
            borderRadius: 3,
            border: '1px solid #334155',
            backgroundColor: '#0f172a',
            boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              background: 'linear-gradient(180deg, #0f172a 0%, rgba(15,23,42,0.95) 100%)',
              backdropFilter: 'blur(10px)',
              px: { xs: 3, sm: 4 },
              pt: 3,
              pb: 2,
              borderBottom: '1px solid #334155'
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: '#94a3b8',
                backgroundColor: 'rgba(30,41,59,0.5)',
                '&:hover': {
                  color: '#ffffff',
                  backgroundColor: 'rgba(30,41,59,0.8)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>

            <Typography
              sx={{
                fontSize: { xs: '28px', sm: '36px' },
                fontWeight: 700,
                background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: 1
              }}
            >
              About Visibeen
            </Typography>
            <Typography
              sx={{
                color: '#e2e8f0',
                fontSize: '16px',
                lineHeight: 1.5
              }}
            >
              Be Seen. Be Chosen. Be Local.
            </Typography>
          </Box>

          {/* Scrollable Content */}
          <Box
            sx={{
              px: { xs: 3, sm: 4 },
              py: 4,
              maxHeight: 'calc(90vh - 140px)',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                background: '#1e293b'
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#334155',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#475569'
              }
            }}
          >
            <Stack spacing={5}>
              {/* Introduction */}
              <Box>
                <SectionText>
                  At Visibeen, we believe visibility is the new currency. Every local business deserves to be discovered where it matters most — on Google. That's why we built Visibeen: a powerful yet simple Local SEO platform that helps you show up, stand out, and succeed.
                </SectionText>
              </Box>

              {/* Pillars Section */}
              <Box>
                <SectionTitle>The Pillars of Visibeen</SectionTitle>
                <SectionText>
                  Our platform is built on four core pillars that guide every feature and every client journey:
                </SectionText>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2
                  }}
                >
                  {pillars.map((pillar, index) => (
                    <CardBox key={index}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <IconWrapper sx={{ backgroundColor: pillar.bgColor }}>
                          {pillar.icon}
                        </IconWrapper>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '16px',
                              fontWeight: 600,
                              color: '#ffffff',
                              mb: 1
                            }}
                          >
                            {pillar.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '13px',
                              lineHeight: '20px',
                              color: '#e2e8f0'
                            }}
                          >
                            {pillar.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardBox>
                  ))}
                </Box>
              </Box>

              {/* Who We Are */}
              <Box>
                <SectionTitle>Who We Are</SectionTitle>
                <SectionText>
                  We're not just a software company — we're a team of marketers, builders, and problem-solvers passionate about helping businesses thrive in the digital-first world. From family-run shops to fast-growing brands, our mission is to make local search easy, transparent, and result-driven.
                </SectionText>
              </Box>

              {/* What We Do */}
              <Box>
                <SectionTitle>What We Do</SectionTitle>
                <SectionText>
                  Visibeen transforms complex SEO into clear, actionable steps. With Visibeen, you don't just get data — you get direction.
                </SectionText>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2
                  }}
                >
                  {features.map((feature, index) => (
                    <CardBox key={index}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box sx={{
                          backgroundColor: '#1e293b',
                          borderRadius: '8px',
                          border: '1px solid #22d3ee',
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {feature.icon}
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontWeight: 600,
                              color: '#ffffff',
                              mb: 0.5
                            }}
                          >
                            {feature.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '13px',
                              color: '#e2e8f0'
                            }}
                          >
                            {feature.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardBox>
                  ))}
                </Box>
              </Box>

              {/* Who We Help */}
              <Box>
                <SectionTitle>Who We Help</SectionTitle>
                <SectionText>
                  Visibeen is designed for businesses of all sizes and industries:
                </SectionText>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2
                  }}
                >
                  {targetAudiences.map((audience, index) => (
                    <CardBox key={index}>
                      <Stack spacing={1.5} alignItems="center" textAlign="center">
                        <Box sx={{ color: '#22d3ee' }}>
                          {audience.icon}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#ffffff'
                          }}
                        >
                          {audience.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: '#e2e8f0'
                          }}
                        >
                          {audience.description}
                        </Typography>
                      </Stack>
                    </CardBox>
                  ))}
                </Box>
              </Box>

              {/* Our Values */}
              <Box>
                <SectionTitle>Our Values</SectionTitle>
                <SectionText>
                  Our work is guided by principles that keep us focused on what matters:
                </SectionText>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: 2
                  }}
                >
                  {values.map((value, index) => (
                    <CardBox key={index}>
                      <Stack spacing={1.5} alignItems="center" textAlign="center">
                        <IconWrapper sx={{ backgroundColor: value.bgColor }}>
                          {value.icon}
                        </IconWrapper>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#ffffff'
                          }}
                        >
                          {value.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: '#e2e8f0'
                          }}
                        >
                          {value.description}
                        </Typography>
                      </Stack>
                    </CardBox>
                  ))}
                </Box>
              </Box>

              {/* Why Visibeen */}
              <Box>
                <SectionTitle>Why Visibeen?</SectionTitle>
                <SectionText>
                  Because being visible online isn't enough. You need to be trusted, chosen, and remembered. Visibeen gives you the tools, insights, and ongoing guidance to make that happen — faster, smarter, and with measurable impact.
                </SectionText>
                <Stack spacing={1.5}>
                  {benefits.map((benefit, index) => (
                    <Stack key={index} direction="row" spacing={2} alignItems="center">
                      <CheckmarkIcon width={18} height={18} color="#4CAF50" />
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#ffffff'
                        }}
                      >
                        {benefit}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AboutUsModal;
